import { useEffect, useState, type ReactNode } from 'react';
import {
  identityApiRef,
  microsoftAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { EmptyState, Progress } from '@backstage/core-components';
import { ADMIN_GROUP_REF } from './constants';

const normalizeRefs = (refs: unknown): string[] =>
  Array.isArray(refs)
    ? refs.map(ref => String(ref).trim().toLowerCase()).filter(Boolean)
    : [];

const decodeEntFromJwt = (token?: string): string[] => {
  if (!token) {
    return [];
  }
  try {
    const [, payload] = token.split('.');
    if (!payload) {
      return [];
    }
    const padded = payload.replace(/-/g, '+').replace(/_/g, '/');
    const decoded = JSON.parse(atob(padded)) as { ent?: unknown };
    return normalizeRefs(decoded.ent);
  } catch {
    return [];
  }
};

export const useIsAdmin = () => {
  const identityApi = useApi(identityApiRef);
  const microsoftAuthApi = useApi(microsoftAuthApiRef);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    let mounted = true;

    void (async () => {
      try {
        const identity = await identityApi.getBackstageIdentity();
        let refs = normalizeRefs(identity?.ownershipEntityRefs);

        // Fallback: session Microsoft (fonte fresca pós-login / restore).
        if (!refs.includes(ADMIN_GROUP_REF)) {
          const microsoftIdentity =
            await microsoftAuthApi.getBackstageIdentity({ optional: true });
          refs = Array.from(
            new Set([
              ...refs,
              ...normalizeRefs(microsoftIdentity?.identity?.ownershipEntityRefs),
            ]),
          );
        }

        // Fallback: claim `ent` no JWT (quando omitIdentityTokenOwnershipClaim=false).
        if (!refs.includes(ADMIN_GROUP_REF)) {
          const { token } = await identityApi.getCredentials();
          refs = Array.from(
            new Set([...refs, ...decodeEntFromJwt(token)]),
          );
        }

        if (!mounted) {
          return;
        }

        setIsAdmin(refs.includes(ADMIN_GROUP_REF));
      } catch {
        if (mounted) {
          setIsAdmin(false);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    })();

    return () => {
      mounted = false;
    };
  }, [identityApi, microsoftAuthApi]);

  return { isAdmin, loading };
};

export const AdminRouteGuard = ({ children }: { children: ReactNode }) => {
  const { isAdmin, loading } = useIsAdmin();

  if (loading) {
    return <Progress />;
  }

  if (!isAdmin) {
    return (
      <EmptyState
        missing="info"
        title="Acesso restrito"
        description="Esta página é restrita a administradores do Developer Portal (backstage_devops)."
      />
    );
  }

  return <>{children}</>;
};
